import React from 'react';
import { AlertTriangleIcon, GiftIcon } from 'lucide-react';
import { Section } from './Shared';
import { DnaHelix, Mandala, TreeOfLife } from './BioDecor';
import { bonuses, tomes } from '../data/kitContents';
const accentStyles: Record<'blue' | 'green' | 'gold', {
  badge: string;
  title: string;
  check: string;
}> = {
  blue: {
    badge: 'bg-blue-900 text-amber-200',
    title: 'text-blue-900',
    check: 'text-blue-700'
  },
  green: {
    badge: 'bg-emerald-800 text-amber-200',
    title: 'text-emerald-800',
    check: 'text-emerald-700'
  },
  gold: {
    badge: 'bg-amber-700 text-amber-50',
    title: 'text-amber-700',
    check: 'text-amber-600'
  }
};
export function PackContents() {
  return <Section id="contenido" className="relative" data-id="element-231" data-mp="922dz0xz1k6b">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" data-id="element-232" data-mp="90r9t00m6aeo">
        <TreeOfLife className="absolute right-[3%] top-8 h-40 w-24 text-emerald-50" data-id="element-233" data-mp="wqvd169w4855" />
        <DnaHelix className="absolute left-[2%] top-1/2 h-48 w-20 text-blue-50" data-id="element-234" data-mp="11lhpzdvme8rc" />
      </div>

      <div className="mb-4 text-center" data-id="element-235" data-mp="3p7o131aobm5w">
        <p className="mb-2 text-sm font-bold uppercase tracking-[.18em] text-amber-700" data-id="element-236" data-mp="3k8thvy8snvl9">✨ A complete practitioner toolkit</p>
        <h2 className="text-3xl font-bold text-blue-900 md:text-5xl" data-id="element-237" data-mp="2sv25wjfas14">
          3 Core Volumes + 5 Strategic Bonuses
        </h2>
        <div className="mt-4 flex flex-wrap justify-center gap-3 text-sm font-semibold text-slate-700 md:text-base" data-id="element-238" data-mp="3u566ihphfj95">
          <span className="rounded-full border border-slate-200 bg-white px-4 py-1.5" data-id="element-239" data-mp="bub55j7wrrvb">📚 3 Volumes + 5 Bonuses</span>
          <span className="rounded-full border border-amber-300 bg-white px-4 py-1.5" data-id="element-240" data-mp="lqe3edzn36cz">📖 245 pages of practical resources</span>
        </div>
      </div>

      <p className="mx-auto mb-10 mt-6 max-w-2xl text-center text-sm leading-relaxed text-slate-700 md:text-base" data-id="element-241" data-mp="gs4c4eh1cpwn">
        Already trained in biodecoding? Bring your intake questions, exploration maps, integration exercises, and
        follow-up resources together in{' '}
        <strong className="text-emerald-700" data-id="element-242" data-mp="3884vj5f6v6i3">one practical collection</strong>.
      </p>

      <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-3" data-id="element-243" data-mp="3c7wy91hwiifj">
        {tomes.map(tome => {
        const accent = accentStyles[tome.accent];
        return <article key={tome.label} className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-7" data-id="element-244" data-mp="3ha4ei39rkkno">
              <div className="mb-4 flex gap-4" data-id="element-245" data-mp="3r6qy6oqct5o4">
                <img src={tome.cover} alt={tome.coverAlt} loading="lazy" draggable={false} className="h-[150px] w-[112px] shrink-0 rounded-lg object-cover object-top shadow-md ring-1 ring-slate-200" data-id="element-246" data-mp="3r3v0n1if4j07" />
                <div className="min-w-0" data-id="element-247" data-mp="3uybx5wdy653">
                  <span className={`inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 text-[11px] font-bold tracking-[.18em] ${accent.badge}`} data-id="element-248" data-mp="8sho809iw8ue">
                    <span aria-hidden="true" data-id="element-249" data-mp="dp85gqx2dahe">{tome.emoji}</span>
                    {tome.label}
                  </span>
                  <h3 className={`mt-3 text-lg font-bold leading-tight md:text-xl ${accent.title}`} data-id="element-250" data-mp="237wze9bjookt">
                    {tome.title}
                  </h3>
                  <p className="mt-1.5 text-xs italic leading-snug text-slate-500" data-id="element-251" data-mp="1y7sfe9bfhbpu">{tome.subtitle}</p>
                </div>
              </div>
              {tome.intro && <p className="mb-4 text-[13px] leading-relaxed text-slate-700" data-id="element-252" data-mp="1obpe15oehh3y">{tome.intro}</p>}
              <div className="mb-4 h-px bg-amber-500/50" aria-hidden="true" data-id="element-253" data-mp="2lz7nyjtssp9j" />
              <ul className="space-y-2.5 text-sm leading-snug text-slate-900" data-id="element-254" data-mp="2gzl5nvz44itv">
                {tome.features.map(feature => <li key={feature} className="flex gap-2.5" data-id="element-255" data-mp="2c8l8cpm2zyqu">
                    <span className={`font-bold ${accent.check}`} data-id="element-256" data-mp="2c6s3nfv13olh">✓</span>
                    <span data-id="element-257" data-mp="2790cio27k6ic">{feature}</span>
                  </li>)}
              </ul>
              {tome.note && <p className="mt-auto pt-4" data-id="element-258" data-mp="34pxmxxg8zrx4">
                  <span className="block rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium leading-relaxed text-slate-900" data-id="element-259" data-mp="2zyfnjdtvhze9">
                    {tome.note}
                  </span>
                </p>}
              {tome.warning && <p className="mt-3 flex gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-[12px] leading-relaxed text-slate-700" data-id="element-260" data-mp="2pgk5d25tdu1i">
                  <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" data-id="element-261" data-mp="2akmenzenthwo" />
                  <span data-id="element-262" data-mp="2fjfjz4jsm2lr">{tome.warning}</span>
                </p>}
            </article>;
      })}
      </div>

      <div className="mb-6 mt-14 text-center" data-id="element-263" data-mp="3390bwcxxca76">
        <h3 className="text-xl font-bold text-blue-900 md:text-2xl" data-id="element-264" data-mp="386a1ptp86nvj">
          🎁 5 STRATEGIC BONUSES — <span className="text-amber-700" data-id="element-265" data-mp="2thw0tobdd9hm">ALWAYS INCLUDED</span>
        </h3>
      </div>

      <div className="mx-auto grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-266" data-mp="2yfp3w8hdlndo">
        {bonuses.map(bonus => <article key={bonus.number} className="flex gap-4 rounded-2xl border border-blue-100 bg-blue-50 p-4" data-id="element-267" data-mp="3m5jgqq1p3bxx">
            <img src={bonus.cover} alt={bonus.coverAlt} loading="lazy" draggable={false} className="h-[124px] w-[93px] shrink-0 rounded-lg object-cover object-top shadow-md ring-1 ring-slate-200" data-id="element-268" data-mp="3qxjlqwwmgv1d" />
            <div className="flex min-w-0 flex-col" data-id="element-269" data-mp="1ahenfaw4pwox">
              <div className="mb-2 flex flex-wrap items-center gap-2" data-id="element-270" data-mp="1agllcn847vd4">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-900 px-2.5 py-1 text-[10px] font-bold tracking-wide text-amber-200" data-id="element-271" data-mp="1p6j58qz0hywi">
                  <GiftIcon className="h-3 w-3" data-id="element-272" data-mp="wi596grhhcnl" />
                  BONUS {bonus.number}
                </span>
                <span className="text-[11px] font-semibold text-amber-700" data-id="element-273" data-mp="rklszbb86pt0">{bonus.pages}</span>
              </div>
              <p className="text-[15px] font-bold leading-tight text-blue-900" data-id="element-274" data-mp="15k4n3077c0mb">{bonus.title}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700" data-id="element-275" data-mp="11gylw6yy4zmp">{bonus.description}</p>
              {bonus.features.length > 0 && <ul className="mt-3 space-y-1.5 text-[13px] leading-snug text-slate-900" data-id="element-276" data-mp="2gwp34mflvpsr">
                  {bonus.features.map(feature => <li key={feature} className="flex gap-2" data-id="element-277" data-mp="19lzocoz9a7yi">
                      <span className="font-bold text-blue-700" data-id="element-278" data-mp="19lhg0tzbk3a8">✓</span>
                      <span data-id="element-279" data-mp="1ekauopqce0bz">{feature}</span>
                    </li>)}
                </ul>}
            </div>
          </article>)}
      </div>

      <div className="relative mt-12 text-center" data-id="element-280" data-mp="1jkykj82u4b4m">
        <Mandala aria-hidden="true" className="pointer-events-none absolute -top-6 left-1/2 h-10 w-10 -translate-x-1/2 text-amber-400/40" data-id="element-281" data-mp="1nmachk96l8fz" />
        <p className="inline-block rounded-full border border-amber-300 bg-white px-5 py-3 text-sm font-medium text-slate-900" data-id="element-282" data-mp="qx2ps8wtstah">
          Everything included with your{' '}
          <span className="font-bold text-emerald-700" data-id="element-283" data-mp="zv4yzbqpqs8u">Biodecoding Practitioner Kit™</span>
        </p>
      </div>
    </Section>;
}